import { Feature, GeoJsonProperties, Geometry } from "geojson";
import { useCallback, useEffect, useRef, useState } from "react";

import { useLocalStorage } from "@nens/react/hooks";
import { objectEntries, objectFromEntries, removeNullUndefined } from "@nens/shared-utils";

import { useRanaFormDerivedState } from "../rana-form-derived-state";
import { Errors, getErrorArray, getErrorString } from "../utils/get-errors";

import { RanaFormProps } from "./rana-form";

export type FormFeatureValue<G extends Geometry | null = Geometry> = Feature<
  G,
  { name: string | null } | GeoJsonProperties
> | null;
export type FormValuesExtend = {
  [key: string | number]: string | number | boolean | FormFeatureValue<Geometry> | Date | null;
};
export type FormErrors = Errors;

// Submit functions can assume all client side validation on validatedValues
// was successful, but if the *backend* returns errors, it should report them
// back using the setErrors function that is passed in.
export type SubmitFunction<FormValues> = (
  validatedValues: FormValues,
  setErrors: (errors: FormErrors) => void
) => void;

export type AsyncSubmitFunction<FormValues> = (
  validatedValues: FormValues,
  setErrors: (errors: FormErrors) => void
) => Promise<void>;

// This function takes in all the form values and gives back a FormErrors object;
// if there are no errors, it's {}. Errors that apply to the whole form should use
// the key "*". There can be only one error per field.
export type ValidationFunction<FormValues> = (values: FormValues) => FormErrors;

export const useRanaForm = <FormValues extends FormValuesExtend>({
  initialValues = {} as FormValues,
  validate = () => ({}),
  doSubmit = () => undefined,
  emptyOnSubmit,
  derivationRules,
}: RanaFormProps<FormValues>) => {
  const [values, setValues] = useRanaFormDerivedState(initialValues, derivationRules);
  const [validatedOnce, setValidatedOnce] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [bbox, setBbox] = useState<[number, number, number, number]>([-180, -90, 180, 90]);
  const [filterGeoByMapBbox, setFilterGeoByMapBbox] = useLocalStorage("filter-geo-by-map", false);
  const [required, setRequired] = useState<string[]>([]);
  const addRequired = useCallback(
    (requiredField: string) =>
      !required.includes(requiredField) && setRequired([...required, requiredField]),
    [required]
  );

  const setPartialValues = useCallback(
    (partialValues: FormValuesExtend) => {
      setValues((prevValues) => {
        const baseValues = { ...prevValues, ...partialValues };

        if (validatedOnce) {
          setErrors(validate(baseValues));
        }
        return baseValues;
      });
    },
    [setValues, validate, validatedOnce]
  );

  // If a value has never been set and its initial values get defined set the value (for example: a value loading from the backend)
  const hasOrHadValueKeysRef = useRef<string[]>([]);
  useEffect(() => {
    const newKeys = Object.entries(values).map(([key, value]) => (value ? key : undefined));
    const allKeys = [...hasOrHadValueKeysRef.current, ...removeNullUndefined(newKeys)];
    hasOrHadValueKeysRef.current = [...new Set(allKeys)];
  }, [values]);

  useEffect(() => {
    objectEntries(initialValues).forEach(([key, initialValue]) => {
      if (initialValue && !hasOrHadValueKeysRef.current.includes(String(key))) {
        setPartialValues({ [key]: initialValue });
      }
    });
  }, [initialValues, setPartialValues]);

  const onCancel = () => setValues(initialValues);

  const handleSubmit = async () => {
    const trimmedValues = objectFromEntries(
      objectEntries(values).map(([key, value]) => [
        String(key),
        typeof value === "string" ? value.trim() : value,
      ])
    ) as FormValues;

    const newErrors = validate(trimmedValues);
    setErrors(newErrors);
    setValidatedOnce(true);

    // If there is only one field link the error to that field
    const setBackendOnError = (errors: Errors) => {
      if (Object.keys(values).length === 1) {
        return setErrors({ [Object.keys(values)[0]]: getErrorString(errors) });
      }
      return setErrors(errors);
    };

    if (getErrorArray(newErrors).length === 0) {
      setSubmitting(true);
      await doSubmit(trimmedValues, setBackendOnError);
      if (emptyOnSubmit) setValues(initialValues);
      setSubmitting(false);
    }
  };

  // Still display any errors that have no linked field
  const otherErrors = errors
    ? objectFromEntries(
        removeNullUndefined(
          objectEntries(errors).map(([key, value]) =>
            Object.keys(values).includes(key + "") ? null : [key, value]
          )
        )
      )
    : null;

  return {
    values,
    errors,
    required,
    addRequired,
    submitting,
    setPartialValues,
    onCancel,
    handleSubmit,
    bbox,
    setBbox,
    filterGeoByMapBbox,
    setFilterGeoByMapBbox,
    otherErrors,
  };
};
